import { numberFormats, trunkSchema, type NumberFormat, type TrunkAuthType, type TrunkSummary } from '@smarton-pbx/shared';
import { MoreHorizontal, Pencil, Plus, RadioTower, Trash2 } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { api } from '../api';
import { CountrySelect, flag } from '../components/pickers';
import { Dialog, EmptyState, Field, Loading, Menu, Notice, opensOnClick, PageHeader, Pill } from '../components/ui';
import { type Column, Cells, TableHead, useTable } from '../components/table';
import { message, rich, t } from '../i18n';
import { useLoad, useSubmit } from '../lib/hooks';
import { SettingsTabs } from './SettingsTabs';

const formatNames: Record<NumberFormat, string> = {
  E164: t('+370 5 200 0101 (with +)'),
  E164_NO_PLUS: t('370 5 200 0101 (without +)'),
  INTERNATIONAL_00: t('00370 5 200 0101 (with 00)'),
};

function TrunkStatus({ trunk }: { trunk: TrunkSummary }) {
  const pill =
    trunk.status === 'UP' ? (
      <Pill kind="success" dot>
        {t('Connected')}
      </Pill>
    ) : trunk.status === 'DOWN' ? (
      <Pill kind="danger" dot>
        {t('Not connected')}
      </Pill>
    ) : (
      <Pill kind="warning" dot>
        {trunk.statusDetail === 'Connecting' ? t('Connecting') : t('Checking')}
      </Pill>
    );
  return (
    <span data-testid="trunk-status" data-status={trunk.status} title={trunk.statusDetail ? message(trunk.statusDetail) : undefined}>
      {pill}
    </span>
  );
}

/** A carrier's connection, in words. */
function statusText(trunk: TrunkSummary): string {
  if (trunk.status === 'UP') return t('Connected');
  if (trunk.status === 'DOWN') return t('Not connected');
  return trunk.statusDetail === 'Connecting' ? t('Connecting') : t('Checking');
}

const connectsBy = (trunk: TrunkSummary) =>
  trunk.authType === 'REGISTRATION' ? t('Account {name}', { name: trunk.username ?? '' }) : t('Address ({count})', { count: trunk.inboundAddresses.length });

function carrierColumns(): Column<TrunkSummary>[] {
  return [
    {
      key: 'carrier',
      label: t('Carrier'),
      fixed: true,
      cell: (trunk) => (
        <div className="person">
          <span className="avatar square" style={{ fontSize: '1.15rem', background: 'var(--surface-2)' }} aria-hidden>
            {flag(trunk.countryCode) || trunk.countryCode}
          </span>
          <div>
            <div className="name">{trunk.name}</div>
            <div className="detail">
              {trunk.provider} · {trunk.host}
            </div>
          </div>
        </div>
      ),
      excel: [
        { label: t('Carrier'), value: (trunk) => trunk.name },
        { label: t('Country'), value: (trunk) => trunk.countryCode },
        { label: t('Provider'), value: (trunk) => trunk.provider },
        { label: t('Address'), value: (trunk) => trunk.host },
      ],
    },
    {
      key: 'status',
      label: t('Status'),
      cell: (trunk) => (
        <>
          <TrunkStatus trunk={trunk} />
          {trunk.status === 'DOWN' && trunk.statusDetail && <div className="status-detail">{message(trunk.statusDetail)}</div>}
        </>
      ),
      value: (trunk) => (trunk.status === 'DOWN' && trunk.statusDetail ? `${statusText(trunk)}: ${message(trunk.statusDetail)}` : statusText(trunk)),
    },
    { key: 'connectsBy', label: t('Connects by'), className: 'hide-mobile muted', cell: connectsBy, value: connectsBy },
    { key: 'numbers', label: t('Numbers'), className: 'tabular', cell: (trunk) => trunk.numberCount, value: (trunk) => trunk.numberCount },
    {
      key: 'texts',
      label: t('Texts'),
      hidden: true,
      className: 'hide-mobile muted',
      cell: (trunk) => (trunk.texts ? t('On') : t('Off')),
      value: (trunk) => (trunk.texts ? t('On') : t('Off')),
    },
  ];
}

export function Carriers() {
  const trunks = useLoad(api.listTrunks);
  // Only admins come here.
  const table = useTable('carriers', carrierColumns(), { name: t('Carriers'), rows: () => trunks.data ?? [] });
  const [editing, setEditing] = useState<TrunkSummary | 'new' | null>(null);
  const [error, setError] = useState<string>();

  // Keep the status current: often while a carrier is connecting, then now and then.
  const connecting = trunks.data?.some((trunk) => trunk.status === 'UNKNOWN') ?? false;
  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') trunks.reload();
    }, connecting ? 3000 : 30_000);
    return () => clearInterval(timer);
  }, [connecting, trunks.reload]);

  async function remove(trunk: TrunkSummary) {
    if (!confirm(t('Remove the carrier {name}?', { name: trunk.name }))) return;
    try {
      await api.deleteTrunk(trunk.id);
      trunks.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div className="stack">
      <SettingsTabs />
      <PageHeader title={t('Carriers')} description={t('The phone companies that carry your calls to and from ordinary phone numbers (SIP trunks).')}>
        <button type="button" onClick={() => setEditing('new')} data-testid="add-carrier">
          <Plus size={16} aria-hidden /> {t('Add carrier')}
        </button>
      </PageHeader>
      {(error ?? trunks.error) && <Notice kind="error">{error ?? trunks.error}</Notice>}
      {!trunks.data ? (
        <Loading />
      ) : trunks.data.length === 0 ? (
        <div className="card">
          <EmptyState icon={RadioTower} title={t('No carriers yet')}>
            <p>{t('Add the SIP trunk details your carrier gave you, then add your numbers.')}</p>
          </EmptyState>
        </div>
      ) : (
        <div className="card table-wrap">
          <table>
            <TableHead table={table} />
            <tbody>
              {trunks.data.map((trunk) => (
                <tr key={trunk.id} data-testid="carrier-row" {...opensOnClick(() => setEditing(trunk))}>
                  <Cells table={table} row={trunk} />
                  <td className="actions">
                    <Menu
                      label={t('Actions for {name}', { name: trunk.name })}
                      trigger={(props) => (
                        <button type="button" className="ghost icon" aria-label={t('Actions for {name}', { name: trunk.name })} {...props}>
                          <MoreHorizontal size={18} aria-hidden />
                        </button>
                      )}
                    >
                      {(close) => (
                        <>
                          <button type="button" role="menuitem" onClick={() => (close(), setEditing(trunk))}>
                            <Pencil size={16} aria-hidden /> {t('Edit')}
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            className="danger-item"
                            disabled={trunk.numberCount > 0}
                            title={trunk.numberCount > 0 ? t('Remove or move its numbers first') : undefined}
                            onClick={() => (close(), void remove(trunk))}
                          >
                            <Trash2 size={16} aria-hidden /> {t('Remove')}
                          </button>
                        </>
                      )}
                    </Menu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="subtle">
        {rich('Carriers that recognise you by address need to send your calls to {host} (SIP over UDP). Calls from addresses not listed on a carrier here are refused.', {
          host: <strong>{window.location.hostname}</strong>,
        })}
      </p>
      <Dialog open={editing !== null} onClose={() => setEditing(null)} title={editing === 'new' ? t('Add carrier') : editing ? t('Edit {name}', { name: editing.name }) : ''}>
        {editing && (
          <CarrierForm
            trunk={editing === 'new' ? null : editing}
            onCancel={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              trunks.reload();
            }}
          />
        )}
      </Dialog>
    </div>
  );
}

function CarrierForm({ trunk, onCancel, onSaved }: { trunk: TrunkSummary | null; onCancel: () => void; onSaved: () => void }) {
  const [name, setName] = useState(trunk?.name ?? '');
  const [provider, setProvider] = useState(trunk?.provider ?? '');
  const [countryCode, setCountryCode] = useState(trunk?.countryCode ?? '');
  const [authType, setAuthType] = useState<TrunkAuthType>(trunk?.authType ?? 'REGISTRATION');
  const [host, setHost] = useState(trunk?.host ?? '');
  const [username, setUsername] = useState(trunk?.username ?? '');
  const [password, setPassword] = useState('');
  const [addresses, setAddresses] = useState<string[]>(trunk?.inboundAddresses ?? []);
  const [numberFormat, setNumberFormat] = useState<NumberFormat>(trunk?.numberFormat ?? 'E164');
  const [fromUser, setFromUser] = useState(trunk?.fromUser ?? '');
  const [fromDomain, setFromDomain] = useState(trunk?.fromDomain ?? '');
  const [outboundProxy, setOutboundProxy] = useState(trunk?.outboundProxy ?? '');
  const [texts, setTexts] = useState(trunk?.texts ?? false);
  const form = useSubmit();

  async function submit(event: FormEvent) {
    event.preventDefault();
    const registration = authType === 'REGISTRATION';
    const input = {
      name,
      provider,
      countryCode,
      authType,
      host,
      username: registration ? username.trim() || null : null,
      // Empty keeps the stored password.
      password: registration && password ? password : null,
      inboundAddresses: registration ? [] : addresses,
      numberFormat,
      fromUser: fromUser.trim() || null,
      fromDomain: fromDomain.trim() || null,
      outboundProxy: outboundProxy.trim() || null,
      texts,
    };
    const saved = await form.submit(trunkSchema, input, (value) => (trunk ? api.updateTrunk(trunk.id, value) : api.createTrunk(value)));
    if (saved) onSaved();
  }

  return (
    <form onSubmit={submit} noValidate autoComplete="off">
      <div className="dialog-body">
        <div className="form-grid">
          <Field label={t('Name')} hint={t('Yours to choose, e.g. Zadarma Lithuania')} error={form.fields.name}>
            <input value={name} onChange={(e) => setName(e.target.value)} autoFocus data-testid="carrier-name" />
          </Field>
          <Field label={t('Carrier company')} error={form.fields.provider}>
            <input value={provider} onChange={(e) => setProvider(e.target.value)} data-testid="carrier-provider" />
          </Field>
          <Field label={t('Country')} hint={t('Numbers it sends without a country code are read as this country’s')} error={form.fields.countryCode}>
            <CountrySelect value={countryCode} onChange={setCountryCode} testId="carrier-country" />
          </Field>
          <Field label={t('Connects by')} error={form.fields.authType}>
            <select value={authType} onChange={(e) => setAuthType(e.target.value as TrunkAuthType)} data-testid="carrier-auth">
              <option value="REGISTRATION">{t('Signing in with an account')}</option>
              <option value="IP">{t('Recognising our address (IP)')}</option>
            </select>
          </Field>
          <Field label={t('Server')} hint={t('From the carrier, e.g. sip.zadarma.com')} error={form.fields.host}>
            <input value={host} onChange={(e) => setHost(e.target.value)} spellCheck={false} data-testid="carrier-host" />
          </Field>
          <Field label={t('Outbound proxy (optional)')} hint={t('Only if the carrier gives one. Sign-in and calls are sent through it.')} error={form.fields.outboundProxy}>
            <input value={outboundProxy} onChange={(e) => setOutboundProxy(e.target.value)} spellCheck={false} data-testid="carrier-outbound-proxy" />
          </Field>
          {authType === 'REGISTRATION' && (
            <>
              <Field label={t('Account name')} hint={t('The SIP login')} error={form.fields.username}>
                <input value={username} onChange={(e) => setUsername(e.target.value)} spellCheck={false} data-testid="carrier-username" />
              </Field>
              <Field
                label={t('Password')}
                hint={trunk?.hasPassword ? t('Leave empty to keep the current one') : t('The SIP password. It is never shown again.')}
                error={form.fields.password}
              >
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  placeholder={trunk?.hasPassword ? '••••••••' : ''}
                  data-testid="carrier-password"
                />
              </Field>
            </>
          )}
          <Field label={t('Numbers we call are sent as')} error={form.fields.numberFormat}>
            <select value={numberFormat} onChange={(e) => setNumberFormat(e.target.value as NumberFormat)}>
              {numberFormats.map((format) => (
                <option key={format} value={format}>
                  {formatNames[format]}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('From user (optional)')} hint={t('Only if the carrier asks for one in the From header')} error={form.fields.fromUser}>
            <input value={fromUser} onChange={(e) => setFromUser(e.target.value)} spellCheck={false} />
          </Field>
          <Field label={t('From domain (optional)')} hint={t('Only if the carrier asks, e.g. this server’s address')} error={form.fields.fromDomain}>
            <input value={fromDomain} onChange={(e) => setFromDomain(e.target.value)} spellCheck={false} data-testid="carrier-from-domain" />
          </Field>
          <Field label={t('Texts (SMS)')} hint={t('Only if the carrier passes texts over SIP (MESSAGE). Then turn them on for each number.')} error={form.fields.texts}>
            <select value={texts ? 'yes' : 'no'} onChange={(e) => setTexts(e.target.value === 'yes')} data-testid="carrier-texts">
              <option value="no">{t('Not passed')}</option>
              <option value="yes">{t('Passed over SIP')}</option>
            </select>
          </Field>
        </div>
        {authType === 'IP' && (
          <Field
            group
            label={t('Carrier addresses')}
            hint={t('Where the carrier sends your calls from: addresses or ranges, e.g. 185.45.152.0/24. Other addresses are refused.')}
            error={form.fields.inboundAddresses ?? Object.entries(form.fields).find(([key]) => key.startsWith('inboundAddresses.'))?.[1]}
          >
            <AddressListInput value={addresses} onChange={setAddresses} />
          </Field>
        )}
        {form.error && <Notice kind="error">{form.error}</Notice>}
      </div>
      <div className="dialog-footer">
        <button type="button" className="secondary" onClick={onCancel}>
          {t('Cancel')}
        </button>
        <button type="submit" disabled={form.busy} data-testid="save-carrier">
          {trunk ? t('Save changes') : t('Add carrier')}
        </button>
      </div>
    </form>
  );
}

/** IPv4 addresses or ranges, as removable chips plus a box to type more. */
function AddressListInput({ value, onChange }: { value: string[]; onChange: (addresses: string[]) => void }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const entries = draft
      .split(/[\s,;]+/)
      .map((entry) => entry.trim())
      .filter((entry) => entry && !value.includes(entry));
    if (entries.length) onChange([...value, ...entries]);
    setDraft('');
  };
  return (
    <div className="chips" data-testid="carrier-addresses">
      {value.map((address) => (
        <span key={address} className="chip tabular">
          {address}
          <button type="button" onClick={() => onChange(value.filter((a) => a !== address))} aria-label={t('Remove {name}', { name: address })}>
            ×
          </button>
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            add();
          }
        }}
        onBlur={add}
        placeholder={value.length ? t('Add another') : t('Type an address and press Enter')}
        aria-label={t('Add a carrier address')}
        spellCheck={false}
      />
    </div>
  );
}
